/**
 * JobCardTemplatesPage — Iter 413 (Dirk, 2026-02-28).
 *
 * Reached from Job Cards page → "Templates" button.
 * URL: /jobcards/templates  (?customer_id=X to pre-filter)
 *
 * What operators can do here:
 *   • See every template (global + customer-specific)
 *   • Filter to a specific customer, or global-only
 *   • Free-text search across name / description
 *   • Create a new global template  ("+ New template")
 *   • Create a customer-specific template ("+ Create template for {name}"
 *     — appears when filter=customer AND no results)
 *   • Open a template → edit its name / description / tasks
 *   • Duplicate → clone as a fresh row
 *   • Delete → moves to Trash pillar (restorable)
 *   • "Use template" → jumps into the New Job Card wizard with the
 *     task scaffold pre-filled.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, Copy, Edit3, Search, Play, Loader2, Users,
  Globe, FileText, X as XIcon,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import SearchableSelect from "../components/SearchableSelect";

export default function JobCardTemplatesPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const customerFilter = params.get("customer_id") || "";
  const [q, setQ] = useState(params.get("q") || "");
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 413 — Optimistically show the "New template" button until we
  // know the current user's role. Prevents an ugly flicker when
  // /auth/me is still in flight on first paint.
  const [me, setMe] = useState({ role: "manager" });

  const filteredCustomerName = useMemo(() => {
    if (!customerFilter || customerFilter === "__global__") return "";
    const c = customers.find((x) => x.id === customerFilter);
    return c?.name || "";
  }, [customerFilter, customers]);

  const customerOptions = useMemo(() => customers.map((c) => ({
    id: c.id,
    label: c.name,
    sublabel: c.external_ref || c.email || c.phone || "",
    searchTerms: [c.external_ref, c.vat_number, c.email, c.phone].filter(Boolean),
  })), [customers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (customerFilter) qp.set("customer_id", customerFilter);
      if (q.trim()) qp.set("q", q.trim());
      const [tRes, cRes, meRes] = await Promise.all([
        api.get(`/jobcard-templates?${qp.toString()}`),
        api.get("/customers").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setTemplates(tRes.data || []);
      const cust = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []);
      setCustomers(cust);
      setMe(meRes.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, [customerFilter, q]);

  useEffect(() => { load(); }, [load]);

  const canWrite = !!me && ["owner", "admin", "manager"].includes(me.role);

  const setCustomerFilter = (cid) => {
    const next = new URLSearchParams(params);
    if (!cid) next.delete("customer_id");
    else next.set("customer_id", cid);
    setParams(next, { replace: true });
  };

  const duplicate = async (t) => {
    try {
      const { data } = await api.post(`/jobcard-templates/${t.id}/duplicate`);
      toast.success(`Duplicated as "${data.name}".`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed.");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Move "${t.name}" to Trash? You can restore it from the tenant Trash later.`)) return;
    try {
      await api.delete(`/jobcard-templates/${t.id}`);
      toast.success("Moved to Trash.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const openInWizard = (t) => {
    // Pre-select the customer when the template is scoped to one.
    const qs = new URLSearchParams();
    qs.set("template_id", t.id);
    if (t.customer_id) qs.set("customer_id", t.customer_id);
    nav(`/jobcards/new?${qs.toString()}`);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="jc-templates-page">
      {/* Header — mobile-safe stacked layout. On < md the description
          gets its own row, then the two action buttons in a bottom
          row. Iter 413.2 (Dirk, 2026-02-28) fix for the cramped
          description-between-buttons layout. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/jobcards" className="hv-btn hv-btn-ghost text-sm" data-testid="jc-templates-back">
            <ArrowLeft className="w-4 h-4" /> Job Cards
          </Link>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Job Cards</div>
            <div className="text-xl font-bold">Templates</div>
          </div>
          {canWrite && (
            <Link
              to={`/jobcards/templates/new${customerFilter && customerFilter !== "__global__" ? `?customer_id=${customerFilter}` : ""}`}
              className="hv-btn hv-btn-primary"
              data-testid="jc-templates-new-btn"
            >
              <Plus className="w-4 h-4" /> New template
            </Link>
          )}
        </div>
        <div className="text-[11px] text-[#a0aabf]">
          Reusable task scaffolds. Everything else (tech, staff, vehicle, dates, special instructions)
          is filled in per normal Job Card flow.
        </div>
      </div>

      {/* Filter bar — Iter 413.2 (Dirk, 2026-02-28) — Replaced the
          native scroll-only <select> customer scope with a SearchableSelect
          typeahead. Users can now TYPE a customer name and jump straight
          to that customer's templates. "Global-only" and "All" are still
          reachable via the quick chips. */}
      <div className="hv-card p-3 space-y-3">
        <div className="flex items-center gap-1.5 flex-wrap text-xs">
          <span className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mr-1">
            Scope:
          </span>
          <ScopeChip
            active={!customerFilter}
            label="All"
            onClick={() => setCustomerFilter("")}
            testId="jc-templates-scope-all"
          />
          <ScopeChip
            active={customerFilter === "__global__"}
            label="Global only"
            onClick={() => setCustomerFilter("__global__")}
            testId="jc-templates-scope-global"
          />
          {customerFilter && customerFilter !== "__global__" && (
            <ScopeChip
              active
              label={`Customer: ${filteredCustomerName || "…"}`}
              onClick={() => setCustomerFilter("")}
              endIcon={<XIcon className="w-3 h-3" />}
              testId="jc-templates-scope-clear-customer"
            />
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 min-w-0">
            Search by customer
            <SearchableSelect
              value={customerFilter && customerFilter !== "__global__" ? customerFilter : ""}
              onChange={(cid) => setCustomerFilter(cid || "")}
              options={customerOptions}
              placeholder="Type customer name…"
              emptyHint="No matching customers."
              testId="jc-templates-customer-search"
            />
          </label>
          <label className="text-[10px] uppercase tracking-wider text-[#647087] font-bold flex flex-col gap-1 min-w-0">
            Search templates
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or description…"
                className="hv-input text-sm pl-7 w-full"
                data-testid="jc-templates-search"
              />
            </div>
          </label>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="hv-card p-6 text-center text-[#a0aabf]">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading templates…
        </div>
      ) : templates.length === 0 ? (
        <EmptyState
          customerFilter={customerFilter}
          filteredCustomerName={filteredCustomerName}
          canWrite={canWrite}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              t={t}
              canWrite={canWrite}
              onUse={() => openInWizard(t)}
              onDuplicate={() => duplicate(t)}
              onDelete={() => remove(t)}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function ScopeChip({ active, label, onClick, endIcon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-[11px] font-bold px-2 py-1 rounded border transition inline-flex items-center gap-1 ${
        active
          ? "bg-[#1FB6C1]/15 text-[#1FB6C1] border-[#1FB6C1]/50"
          : "bg-white/[0.02] text-[#a0aabf] border-white/10 hover:border-[#1FB6C1]/30"
      }`}
    >
      {label}
      {endIcon}
    </button>
  );
}


function TemplateCard({ t, canWrite, onUse, onDuplicate, onDelete }) {
  const isGlobal = t.is_global !== false && !t.customer_id;
  return (
    <div className="hv-card p-3 space-y-2" data-testid={`jc-template-card-${t.id}`}>
      <div className="flex items-start gap-2">
        <FileText className="w-4 h-4 text-[#1FB6C1] shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <Link
            to={`/jobcards/templates/${t.id}`}
            className="text-sm font-bold text-white hover:text-[#1FB6C1] break-words block"
            data-testid={`jc-template-name-${t.id}`}
          >
            {t.name}
          </Link>
          {t.description && (
            <div className="text-[11px] text-[#a0aabf] mt-0.5 break-words line-clamp-2">
              {t.description}
            </div>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {isGlobal ? (
              <span className="text-[9px] uppercase font-mono tracking-wider px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.02] text-[#a0aabf] inline-flex items-center gap-1">
                <Globe className="w-2.5 h-2.5" /> Global
              </span>
            ) : (
              <span className="text-[9px] uppercase font-mono tracking-wider px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 inline-flex items-center gap-1">
                <Users className="w-2.5 h-2.5" /> {t.customer_name || "Customer-specific"}
              </span>
            )}
            <span className="text-[10px] text-[#647087]">
              {t.task_count} task{t.task_count === 1 ? "" : "s"}
            </span>
            {t.usage_count > 0 && (
              <span className="text-[10px] text-[#647087]">· used {t.usage_count}×</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-white/5">
        <button
          onClick={onUse}
          className="hv-btn hv-btn-primary text-xs"
          data-testid={`jc-template-use-${t.id}`}
        >
          <Play className="w-3 h-3" /> Use
        </button>
        <Link
          to={`/jobcards/templates/${t.id}`}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid={`jc-template-edit-${t.id}`}
        >
          <Edit3 className="w-3 h-3" /> Edit
        </Link>
        {canWrite && (
          <>
            <button
              onClick={onDuplicate}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`jc-template-dup-${t.id}`}
            >
              <Copy className="w-3 h-3" /> Duplicate
            </button>
            <button
              onClick={onDelete}
              className="hv-btn hv-btn-ghost text-xs ml-auto text-rose-300/70 hover:text-rose-300"
              data-testid={`jc-template-del-${t.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}


function EmptyState({ customerFilter, filteredCustomerName, canWrite }) {
  if (customerFilter && customerFilter !== "__global__" && filteredCustomerName) {
    return (
      <div className="hv-card p-8 text-center space-y-3" data-testid="jc-templates-empty-customer">
        <div className="text-sm text-[#a0aabf]">
          No templates for <strong className="text-white">{filteredCustomerName}</strong>.
        </div>
        {canWrite && (
          <Link
            to={`/jobcards/templates/new?customer_id=${customerFilter}`}
            className="hv-btn hv-btn-primary inline-flex text-sm"
            data-testid="jc-templates-empty-customer-create"
          >
            <Plus className="w-4 h-4" /> Create template for {filteredCustomerName}
          </Link>
        )}
      </div>
    );
  }
  return (
    <div className="hv-card p-8 text-center space-y-3" data-testid="jc-templates-empty">
      <div className="text-sm text-[#a0aabf]">No templates yet.</div>
      {canWrite && (
        <Link
          to="/jobcards/templates/new"
          className="hv-btn hv-btn-primary inline-flex text-sm"
          data-testid="jc-templates-empty-create"
        >
          <Plus className="w-4 h-4" /> Create your first template
        </Link>
      )}
    </div>
  );
}
